import React from "react";
import { ArrowUpRight } from "lucide-react";
import { aspectoLabel, stripGoogleTranslation } from "../socialFormat";

interface Props {
  activePlatform: string;
  showGoogleRatings: boolean;
  windowDays: number;
  positiveAspectList: Array<{ label: string; count: number; pct: number }>;
  positiveTestimonials: any[];
  renderStars: (rating: number, color?: string) => React.ReactNode;
  renderPlatformBadge: () => React.ReactNode;
  getDirectLink: (row: any) => string;
  onSelectCommentForModal: (row: any) => void;
}

export function SocialOpportunitySection({
  activePlatform,
  showGoogleRatings,
  windowDays,
  positiveAspectList,
  positiveTestimonials,
  renderStars,
  renderPlatformBadge,
  getDirectLink,
  onSelectCommentForModal,
}: Props) {
  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);

  const isMetaIg = activePlatform === "meta_ig" || activePlatform === "instagram";
  const isMetaFb = activePlatform === "meta_fb" || activePlatform === "facebook";
  const isGoogle = activePlatform === "gbp" || activePlatform === "google";

  const platformBadgeLabel = isMetaIg
    ? "Instagram"
    : isMetaFb
    ? "Facebook"
    : isGoogle
    ? "Google BP"
    : "Visión Global";

  return (
    <section id="section-social-opportunity" className="social-editorial-section">
      <div className="social-section-tag">OPORTUNIDAD</div>
      <h2 className="social-section-title">
        {!showGoogleRatings ? "Comentarios que generan confianza" : "Reseñas que venden"}
      </h2>
      <p className="social-section-subtitle">
        {!showGoogleRatings
          ? `Los temas más elogiados y comentarios positivos recientes en ${platformBadgeLabel}, para destacar como prueba social.`
          : "Los temas más elogiados y las reseñas positivas más recientes de cada uno, para usar como prueba social con permiso del autor."}
      </p>

      <div className="social-editorial-badges">
        {renderPlatformBadge()}
        <span className="social-role-badge">Marketing</span>
      </div>

      {/* Temas más elogiados dinámicos */}
      <div className="social-trend-box" style={{ marginBottom: "16px" }}>
        <h4 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
          Temas más elogiados
        </h4>
        <p style={{ margin: "0 0 16px 0", fontSize: "11.5px", color: "var(--muted, #8e96a8)" }}>
          Menciones en comentarios y reseñas positivas de los últimos {windowDays} días
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {positiveAspectList.length === 0 ? (
            <div style={{ padding: "16px 0", color: "var(--muted, #8e96a8)", fontSize: "12px" }}>
              No se registran menciones positivas clasificadas en el período seleccionado.
            </div>
          ) : (
            positiveAspectList.map((row) => (
              <div key={row.label} style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "12px" }}>
                <span style={{ width: "160px", color: "var(--muted, #8e96a8)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</span>
                <div style={{ flex: 1, height: "14px", background: "rgba(255, 255, 255, 0.06)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${row.pct}%`, background: "var(--blue, #4285f4)", borderRadius: "4px" }} />
                </div>
                <span style={{ width: "20px", textAlign: "right", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
                  {row.count}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Tarjetas de Prueba Social */}
      <div className="social-cards-grid-4">
        {positiveTestimonials.length === 0 ? (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              color: "var(--muted, #8e96a8)",
              fontSize: "13px",
              gridColumn: "1 / -1",
              background: "var(--surface, #11141b)",
              borderRadius: "10px",
              border: "1px solid var(--line, rgba(255, 255, 255, 0.06))",
            }}
          >
            No se registran testimonios con texto extenso en el período seleccionado.
          </div>
        ) : (
          positiveTestimonials.map((item: any) => {
          const itemPlat = text(item.platform, isGoogle ? "gbp" : isMetaIg ? "meta_ig" : isMetaFb ? "meta_fb" : "gbp").toLowerCase();
          const isGbpTestimonial = itemPlat === "gbp";
          const channelName = text(item.post_title) || "Canal principal";
          const asp = item.aspecto ? aspectoLabel(text(item.aspecto)) : "Experiencia";
          const msg = text(item.message) || item.text;
          const cleanedMessage = stripGoogleTranslation(msg);
          const isLong = cleanedMessage.length > 175;

          return (
            <div key={item.id || text(item.feedback_id)} className="social-editorial-card border-positive">
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <div>
                    {isGbpTestimonial ? (
                      renderStars(5)
                    ) : (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "2px 7px",
                          borderRadius: "4px",
                          background: itemPlat.includes("ig")
                            ? "rgba(225, 48, 108, 0.15)"
                            : itemPlat.includes("fb")
                            ? "rgba(24, 119, 242, 0.15)"
                            : "rgba(52, 168, 83, 0.15)",
                          color: itemPlat.includes("ig") ? "#e1306c" : itemPlat.includes("fb") ? "#1877f2" : "#34a853",
                          marginRight: "6px",
                        }}
                      >
                        {itemPlat.includes("ig") ? "Instagram" : itemPlat.includes("fb") ? "Facebook" : "Google BP"}
                      </span>
                    )}
                    <span style={{ fontSize: "11px", color: "var(--muted, #8e96a8)", marginLeft: isGbpTestimonial ? "6px" : 0 }}>{channelName}</span>
                  </div>
                  <span style={{ fontSize: "10.5px", color: "var(--green, #2ed573)", fontWeight: 650 }}>{asp}</span>
                </div>
                <p style={{ margin: "6px 0 8px 0", fontSize: "12px", color: "var(--text, #f6f7fb)", lineHeight: 1.45 }}>
                  "{isLong ? `${cleanedMessage.slice(0, 175)}…` : cleanedMessage}"
                </p>

                {isLong && (
                  <button
                    type="button"
                    onClick={() => onSelectCommentForModal(item)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--blue, #7d96ff)",
                      fontSize: "11.5px",
                      cursor: "pointer",
                      padding: "0 0 6px 0",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    Ver comentario completo
                  </button>
                )}
              </div>
              <div style={{ textAlign: "right", borderTop: "1px solid var(--line, rgba(255, 255, 255, 0.06))", paddingTop: "8px" }}>
                <a
                  href={getDirectLink(item)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "11px", color: "var(--muted, #8e96a8)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "3px" }}
                >
                  {isGbpTestimonial ? "Ver en Maps" : itemPlat.includes("ig") ? "Ver en IG" : itemPlat.includes("fb") ? "Ver en FB" : "Ver publicación"} <ArrowUpRight size={11} />
                </a>
              </div>
            </div>
          );
        }) )}
      </div>
    </section>
  );
}
